import React, { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../../components/Admin/AdminContext';
import { 
  Bot, CheckCircle, XCircle, Clock, RefreshCw, Eye, 
  MapPin, Tag, FileText, User, AlertTriangle, Sparkles,
  ThumbsUp, ThumbsDown, ChevronDown, ChevronUp, Image,
  Zap, Activity, BarChart3, Map, Camera, Navigation,
  ScanSearch, Filter, ExternalLink
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminAIModeration() {
  const { adminToken } = useAdmin();
  const [spots, setSpots] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [error, setError] = useState(null);
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [expandedSpots, setExpandedSpots] = useState({});
  const [filterVerdict, setFilterVerdict] = useState('all');
  const [scanningSpot, setScanningSpot] = useState(null);
  const [citiesCache, setCitiesCache] = useState({});

  const getAuthHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${adminToken}`
  }), [adminToken]);

  // Reverse Geocoding pour obtenir le nom de la ville
  const getCityFromCoordinates = useCallback(async (lat, lng) => {
    const cacheKey = `${lat},${lng}`;
    
    // Vérifier le cache d'abord
    if (citiesCache[cacheKey]) {
      return citiesCache[cacheKey];
    }

    try {
      // Utiliser Nominatim (OpenStreetMap) pour le reverse geocoding
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
        {
          headers: {
            'User-Agent': 'SunSpot Admin Panel'
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        const city = data.address?.city || 
                     data.address?.town || 
                     data.address?.village || 
                     data.address?.municipality ||
                     data.address?.county ||
                     'Lieu inconnu';
        const country = data.address?.country || '';
        const location = country ? `${city}, ${country}` : city;
        
        // Mettre en cache
        setCitiesCache(prev => ({ ...prev, [cacheKey]: location }));
        return location;
      }
    } catch (error) {
      console.error('Erreur reverse geocoding:', error);
    }
    
    return null;
  }, [citiesCache]);

  // Charger les villes pour les spots expandés
  useEffect(() => {
    spots.forEach(spot => {
      if (expandedSpots[spot.id] && spot.spot_lat && spot.spot_lng) {
        getCityFromCoordinates(spot.spot_lat, spot.spot_lng);
      }
    });
  }, [spots, expandedSpots, getCityFromCoordinates]);

  const fetchSpots = useCallback(async () => {
    try {
      let url = `${API_URL}/api/ai/geo/all?limit=100`;
      if (filterVerdict && filterVerdict !== 'all') {
        url += `&filter_verdict=${filterVerdict}`;
      }
      
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Erreur lors du chargement');
      const data = await response.json();
      setSpots(data.spots || []);
      setStats(data.stats || {});
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [adminToken, filterVerdict, getAuthHeaders]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchSpots();
      setLoading(false);
    };
    loadData();
  }, [fetchSpots]);

  const handleApprove = async (spotId, useAiContent = true) => {
    setActionLoading(prev => ({ ...prev, [spotId]: 'approving' }));
    try {
      const response = await fetch(
        `${API_URL}/api/ai/moderation/approve/${spotId}?use_ai_content=${useAiContent}`,
        {
          method: 'POST',
          headers: getAuthHeaders()
        }
      );
      if (!response.ok) throw new Error('Erreur lors de l\'approbation');
      fetchSpots();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(prev => ({ ...prev, [spotId]: null }));
    }
  };

  const handleReject = async () => {
    if (!selectedSpot || !rejectReason.trim()) return;
    
    const spotId = selectedSpot.id;
    setActionLoading(prev => ({ ...prev, [spotId]: 'rejecting' }));
    
    try {
      const response = await fetch(
        `${API_URL}/api/ai/moderation/reject/${spotId}`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ reason: rejectReason })
        }
      );
      if (!response.ok) throw new Error('Erreur lors du rejet');
      
      setShowRejectDialog(false);
      setRejectReason('');
      setSelectedSpot(null);
      fetchSpots();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(prev => ({ ...prev, [spotId]: null }));
    }
  };

  const handleGeoScan = async (spotId) => {
    // Message de confirmation
    const confirmed = window.confirm(
      '🔄 Lancer un nouveau scan géographique ?\n\n' +
      'Le spot sera analysé par l\'IA dans les prochaines minutes.\n' +
      'Vous pourrez voir les résultats en rafraîchissant la page ou en revenant plus tard.'
    );
    
    if (!confirmed) return;
    
    setScanningSpot(spotId);
    try {
      const response = await fetch(
        `${API_URL}/api/ai/geo/scan/${spotId}`,
        {
          method: 'POST',
          headers: getAuthHeaders()
        }
      );
      
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Erreur lors de la demande de scan');
      }
      
      // Message de succès
      alert(
        '✅ Demande de scan enregistrée !\n\n' +
        'Le worker IA va traiter ce spot dans les 30-60 prochaines secondes.\n\n' +
        '💡 Rafraîchissez la page dans quelques instants pour voir les nouveaux résultats.'
      );
      
      // Refresh la liste après 2 secondes pour laisser le temps au worker
      setTimeout(() => {
        fetchSpots();
      }, 2000);
      
    } catch (err) {
      setError(err.message);
      alert(`❌ Erreur: ${err.message}`);
    } finally {
      setScanningSpot(null);
    }
  };

  const toggleExpand = (spotId) => {
    setExpandedSpots(prev => ({
      ...prev,
      [spotId]: !prev[spotId]
    }));
  };

  const getVerdictBadge = (verdict) => {
    const configs = {
      'VALIDE': { color: 'bg-green-500', icon: CheckCircle, text: 'Validé' },
      'SUSPECT': { color: 'bg-red-500', icon: AlertTriangle, text: 'Suspect' },
      'A_VERIFIER': { color: 'bg-yellow-500', icon: Clock, text: 'À vérifier' }
    };
    const config = configs[verdict] || { color: 'bg-gray-500', icon: Clock, text: verdict || 'Non scanné' };
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs text-gray-900 ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.text}
      </span>
    );
  };

  const getModerationBadge = (status) => {
    const configs = {
      'approved': { color: 'bg-green-600', text: 'Approuvé' },
      'rejected': { color: 'bg-red-600', text: 'Rejeté' },
      'pending': { color: 'bg-yellow-600', text: 'En attente' }
    };
    const config = configs[status] || { color: 'bg-gray-600', text: status || 'N/A' };
    
    return (
      <span className={`px-2 py-1 rounded text-xs text-gray-900 ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Map className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Validation Géographique</h1>
            <p className="text-gray-600">Vérifiez la cohérence entre photos et coordonnées GPS</p>
          </div>
        </div>
        
        <Button
          onClick={fetchSpots}
          variant="outline"
          className="flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card 
            className={`cursor-pointer transition-all ${filterVerdict === 'all' ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => setFilterVerdict('all')}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-blue-600 mb-1">
                <BarChart3 className="w-5 h-5" />
                <span className="text-sm">Total</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">{stats.total || 0}</div>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all ${filterVerdict === 'VALIDE' ? 'ring-2 ring-green-500' : ''}`}
            onClick={() => setFilterVerdict('VALIDE')}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-green-600 mb-1">
                <CheckCircle className="w-5 h-5" />
                <span className="text-sm">Validés</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">{stats.valide || 0}</div>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all ${filterVerdict === 'SUSPECT' ? 'ring-2 ring-red-500' : ''}`}
            onClick={() => setFilterVerdict('SUSPECT')}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-red-600 mb-1">
                <AlertTriangle className="w-5 h-5" />
                <span className="text-sm">Suspects</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">{stats.suspect || 0}</div>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all ${filterVerdict === 'A_VERIFIER' ? 'ring-2 ring-yellow-500' : ''}`}
            onClick={() => setFilterVerdict('A_VERIFIER')}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-yellow-600 mb-1">
                <Clock className="w-5 h-5" />
                <span className="text-sm">À vérifier</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">{stats.a_verifier || 0}</div>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all`}
            onClick={() => setFilterVerdict('all')}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <ScanSearch className="w-5 h-5" />
                <span className="text-sm">Non scannés</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">{stats.sans_validation || 0}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Spots List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      ) : spots.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Map className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600">Aucun spot trouvé pour ce filtre</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {spots.map((spot) => (
            <Card key={spot.id} className="overflow-hidden">
              {/* Spot Header */}
              <div 
                className="flex items-center gap-4 p-4 cursor-pointer hover:bg-white/50 transition-colors"
                onClick={() => toggleExpand(spot.id)}
              >
                {/* Cover Image */}
                <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-700 flex-shrink-0">
                  {spot.cover_photo_url ? (
                    <img
                      src={spot.cover_photo_url}
                      alt={spot.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image className="w-8 h-8 text-gray-500" />
                    </div>
                  )}
                </div>
                
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {getVerdictBadge(spot.geo_validation?.verdict)}
                    {getModerationBadge(spot.moderation_status)}
                    {spot.geo_validation?.score !== undefined && (
                      <span className="text-xs text-gray-600">
                        Score: {spot.geo_validation.score}/100
                      </span>
                    )}
                  </div>
                  <h3 className="text-gray-900 font-medium truncate">{spot.title}</h3>
                  <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {spot.spot_lat?.toFixed(4)}, {spot.spot_lng?.toFixed(4)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Camera className="w-3 h-3" />
                      {spot.photos?.length || 0} photo(s)
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {spot.creator_username}
                    </span>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`https://platform.sun-spot.app/spot/${spot.id}?details=true`, '_blank');
                    }}
                    title="Voir le rendu sur la plateforme"
                    className="text-green-600 border-green-400 hover:bg-green-400/10"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGeoScan(spot.id);
                    }}
                    disabled={scanningSpot === spot.id}
                    title={scanningSpot === spot.id ? "Demande en cours..." : "Relancer le scan géographique (traité par le worker IA)"}
                    className="text-blue-600 border-blue-400 hover:bg-blue-400/10"
                  >
                    <ScanSearch className={`h-4 w-4 ${scanningSpot === spot.id ? 'animate-pulse' : ''}`} />
                  </Button>
                  
                  {spot.moderation_status !== 'approved' && (
                    <>
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApprove(spot.id, true);
                        }}
                        disabled={actionLoading[spot.id]}
                        className="bg-green-600 hover:bg-green-500"
                      >
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedSpot(spot);
                          setShowRejectDialog(true);
                        }}
                        disabled={actionLoading[spot.id]}
                      >
                        <XCircle className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  
                  {expandedSpots[spot.id] ? (
                    <ChevronUp className="w-5 h-5 text-gray-600" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-600" />
                  )}
                </div>
              </div>
              
              {/* Expanded Content */}
              {expandedSpots[spot.id] && (
                <CardContent className="border-t border-gray-200 p-4 space-y-4">
                  {/* Photos Grid - pour comparaison visuelle */}
                  {spot.photos && spot.photos.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <Camera className="w-4 h-4" />
                        Photos du spot (à comparer avec la localisation)
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {spot.photos.map((photo, idx) => (
                          <div 
                            key={idx} 
                            className="aspect-video rounded-lg overflow-hidden bg-white cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all"
                            onClick={() => {
                              setSelectedSpot(spot);
                              setShowPhotoDialog(true);
                            }}
                          >
                            <img
                              src={photo}
                              alt={`Photo ${idx + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Geo Validation Details */}
                  {spot.geo_validation && (
                    <div className={`p-4 rounded-lg ${
                      spot.geo_validation.verdict === 'VALIDE' ? 'bg-green-900/30 border border-green-500/30' :
                      spot.geo_validation.verdict === 'SUSPECT' ? 'bg-red-900/30 border border-red-500/30' :
                      'bg-yellow-900/30 border border-yellow-500/30'
                    }`}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                          <Navigation className="w-4 h-4" />
                          Résultat de la validation géographique
                        </h4>
                        <span className="text-xs text-gray-600">
                          {formatDate(spot.geo_validation.validated_at)}
                        </span>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Verdict</p>
                          <p className="text-lg font-bold text-gray-900">{spot.geo_validation.verdict}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Score de confiance</p>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
                              <div 
                                className={`h-full transition-all ${
                                  spot.geo_validation.score >= 70 ? 'bg-green-500' :
                                  spot.geo_validation.score >= 40 ? 'bg-yellow-500' :
                                  'bg-red-500'
                                }`}
                                style={{ width: `${spot.geo_validation.score}%` }}
                              />
                            </div>
                            <span className="text-gray-900 font-medium">{spot.geo_validation.score}/100</span>
                          </div>
                        </div>
                      </div>
                      
                      {spot.geo_validation.reasons && spot.geo_validation.reasons.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm text-gray-600 mb-1">Raisons</p>
                          <ul className="text-sm text-gray-700 space-y-1">
                            {spot.geo_validation.reasons.map((reason, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <span className="text-gray-500">•</span>
                                {reason}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* AI Optimizations if available */}
                  {(spot.ai_optimized_title || spot.ai_suggested_tags?.length > 0) && (
                    <div className="bg-purple-900/20 border border-purple-500/30 p-4 rounded-lg">
                      <h4 className="text-sm font-medium text-purple-300 mb-3 flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        Suggestions IA disponibles
                      </h4>
                      
                      {spot.ai_optimized_title && (
                        <div className="mb-3">
                          <p className="text-xs text-gray-600 mb-1">Titre suggéré</p>
                          <p className="text-gray-900">{spot.ai_optimized_title}</p>
                        </div>
                      )}
                      
                      {spot.ai_suggested_tags && spot.ai_suggested_tags.length > 0 && (
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Tags suggérés</p>
                          <div className="flex flex-wrap gap-1">
                            {spot.ai_suggested_tags.map((tag, i) => (
                              <span key={i} className="px-2 py-1 bg-purple-700/50 text-purple-200 text-xs rounded">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Coordinates and Location */}
                  <div className="space-y-2">
                    {/* City Name from coordinates */}
                    {citiesCache[`${spot.spot_lat},${spot.spot_lng}`] && (
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="w-4 h-4 text-gray-500" />
                        <span className="font-medium text-gray-900">
                          {citiesCache[`${spot.spot_lat},${spot.spot_lng}`]}
                        </span>
                        <span className="text-gray-500">
                          ({spot.spot_lat.toFixed(4)}, {spot.spot_lng.toFixed(4)})
                        </span>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-4 text-sm">
                      <a 
                        href={`https://www.google.com/maps?q=${spot.spot_lat},${spot.spot_lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-blue-600 hover:underline"
                      >
                        <Map className="w-4 h-4" />
                        Google Maps
                      </a>
                      <a 
                        href={`https://earth.google.com/web/@${spot.spot_lat},${spot.spot_lng},0a,1000d,1y,0h,0t,0r`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-green-600 hover:underline"
                      >
                        <Navigation className="w-4 h-4" />
                        Google Earth
                      </a>
                      <span className="text-gray-600">Créé le {formatDate(spot.created_at)}</span>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Rejeter le spot</DialogTitle>
            <DialogDescription className="text-gray-600">
              {selectedSpot?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              placeholder="Raison du rejet..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="bg-gray-50 border-gray-200 text-gray-900"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Annuler
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReject}
              disabled={!rejectReason.trim()}
            >
              Rejeter
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Dialog */}
      <Dialog open={showPhotoDialog} onOpenChange={setShowPhotoDialog}>
        <DialogContent className="bg-white border-gray-200 max-w-4xl">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Photos du spot - {selectedSpot?.title}</DialogTitle>
            <DialogDescription className="text-gray-600">
              Comparez ces photos avec la localisation GPS: {selectedSpot?.spot_lat?.toFixed(6)}, {selectedSpot?.spot_lng?.toFixed(6)}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4 max-h-[60vh] overflow-y-auto">
            {selectedSpot?.photos?.map((photo, idx) => (
              <div key={idx} className="rounded-lg overflow-hidden">
                <img
                  src={photo}
                  alt={`Photo ${idx + 1}`}
                  className="w-full h-auto"
                />
              </div>
            ))}
          </div>
          <div className="flex justify-between items-center">
            <a 
              href={`https://www.google.com/maps?q=${selectedSpot?.spot_lat},${selectedSpot?.spot_lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline flex items-center gap-1"
            >
              <MapPin className="w-4 h-4" />
              Ouvrir dans Google Maps
            </a>
            <Button variant="outline" onClick={() => setShowPhotoDialog(false)}>
              Fermer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
